import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { PasswordInput } from 'ui/Input';
import NavigationTitle from 'ui/NavigationTitle/NavigationTitle';
import { DEVICE_ID } from 'utils/constants';
import routePaths from 'routes/routePaths';
import api from 'api/api';
import { requiredOnly, validateConfirmPassword, validatePassword } from 'utils/validation';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { AxiosStatus } from 'api/axiosConfig';
import { StatusModal } from 'components/modals';
import {
  IResetPasswordResponse,
  IResetPasswordError,
  IResetPasswordFormData,
} from './resetPassword.model';

import styles from './ResetPassword.module.css';

const schema = yup.object().shape({
  password: requiredOnly('defaultPassword'),
  newPassword: validatePassword(),
  confirmPassword: validateConfirmPassword('newPassword'),
});
const ResetPassword = () => {
  const [success, setSuccess] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.login);
  };

  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const validate = useCallback((values: IResetPasswordFormData) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      const valErrs = mapYupObjectError<IResetPasswordFormData>(e);
      return objectMap(valErrs, (v) => v[0]);
    }
    return undefined;
  }, []);

  const onSubmit = useCallback(
    (values: IResetPasswordFormData) => {
      const body = {
        ...values,
        deviceID: DEVICE_ID,
        loginId: sessionStorage.getItem('userName'),
      };
      // console.log('request', body);
      return api
        .post('customer/resetDefaultPassword', body)
        .then((response: IResetPasswordResponse) => {
          setSuccess(response.description || 'Password has been successfully reset!');
          setSubmitStatus(AxiosStatus.SUCCESS);
        })
        .catch((error: IResetPasswordError) => {
          setErrorMsg(error.description);
          setSubmitStatus(AxiosStatus.FAILURE);
          return Promise.reject(error.description);
        });
    },
    [],
  );

  return (
    <div className={styles.content}>
      <NavigationTitle to={routePaths.forgotPassword}>Create a new Password</NavigationTitle>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <p className={styles.passwordDesc}>
              Enter the default password sent to you.
            </p>
            <PasswordInput
              name="password"
              label="Default Password"
              className={styles.field}
              disabled={submitting}
            />
            <p className={styles.passwordDesc}>
              Should have 8 characters with at least one uppercase and one special character.
            </p>
            <PasswordInput name="newPassword" label="New Password" className={styles.field} disabled={submitting} />
            <PasswordInput
              name="confirmPassword"
              label="New Password Confirmation"
              className={styles.field}
              disabled={submitting}
            />
            <Button type="submit" className={styles.submit} fullWidth disabled={submitting || hasValidationErrors}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
            </Button>
          </form>
        )}
      />

      <StatusModal
        open={showStatusModal}
        status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
        onClose={onCloseStatusModal}
        onRetry={onCloseStatusModal}
        onRetryText="Close"
        statusText="Continue to Login"
      >
        {submitStatus === AxiosStatus.SUCCESS ? success : errorMsg}
      </StatusModal>
    </div>
  );
};

export default ResetPassword;
